import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { patientService } from '../services/patientService';

const MyPatients = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('grid');
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPatients, setTotalPatients] = useState(0);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Not available';
    }
  };

  // Transform API patient data to UI format
  const transformPatientData = (apiPatient) => {
    const name = apiPatient.fullName || `${apiPatient.firstName || ''} ${apiPatient.lastName || ''}`.trim() || 'Unnamed';
    return {
      id: apiPatient._id,
      name,
      age: apiPatient.age || 'N/A',
      gender: apiPatient.gender || 'Not specified',
      lastAppointment: formatDate(apiPatient.updatedAt || apiPatient.createdAt),
      location: apiPatient.address || 'Location not provided',
      email: apiPatient.email,
      phoneNumber: apiPatient.phoneNumber,
      domain: apiPatient.selectedDomain?.name || (typeof apiPatient.selectedDomain === 'string' ? apiPatient.selectedDomain : ''),
      status: apiPatient.status,
      profileImage: apiPatient.profileImage,
      rawData: apiPatient
    };
  };

  // Fetch patients from API
  const fetchPatients = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        gender: genderFilter,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      };

      const response = await patientService.getPatients(params);

      if (response && Array.isArray(response)) {
        const transformedPatients = response.map(transformPatientData);
        setPatients(transformedPatients);
        setTotalPatients(transformedPatients.length);
      } else if (response && Array.isArray(response.data)) {
        const transformedPatients = response.data.map(transformPatientData);
        setPatients(transformedPatients);
        setTotalPatients(response.total || transformedPatients.length);
      } else {
        setPatients([]);
        setTotalPatients(0);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load patients from server');
      setPatients([]);
      setTotalPatients(0);
    } finally {
      setLoading(false);
    }
  };

  // Debounced load — single effect to avoid duplicate/hanging fetches
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPatients();
    }, searchTerm || genderFilter ? 400 : 0);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, searchTerm, genderFilter]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilterDropdown && !event.target.closest('.filter-dropdown')) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterDropdown]);

  const totalPages = Math.ceil(totalPatients / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPatients = patients.slice(startIndex, endIndex);

  const PatientCard = ({ patient }) => (
    <div
      onClick={() => navigate(`/patient/${patient.id}`)}
      className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          {patient.profileImage ? (
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-gray-200">
              <img
                src={patient.profileImage}
                alt={patient.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to initials if image fails to load
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <div class="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
                      <span class="text-white font-semibold text-sm">
                        ${patient.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                  `;
                }}
              />
            </div>
          ) : (
            <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {(patient.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2)}
              </span>
            </div>
          )}
          <div>
            <h3 className="font-semibold text-sm text-black">{patient.name}</h3>
            <p className="text-xs text-gray-500">{patient.age}, {patient.gender}</p>
            {patient.domain && (
              <span className="inline-block mt-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">
                {patient.domain}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {patient.status && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              patient.status === 'completed' ? 'bg-green-100 text-green-700' :
              patient.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {patient.status}
            </span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Add menu logic here
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 2.67C8.37 2.67 8.67 2.37 8.67 2C8.67 1.63 8.37 1.33 8 1.33C7.63 1.33 7.33 1.63 7.33 2C7.33 2.37 7.63 2.67 8 2.67Z" fill="currentColor"/>
              <path d="M8 8.67C8.37 8.67 8.67 8.37 8.67 8C8.67 7.63 8.37 7.33 8 7.33C7.63 7.33 7.33 7.63 7.33 8C7.33 8.37 7.63 8.67 8 8.67Z" fill="currentColor"/>
              <path d="M8 14.67C8.37 14.67 8.67 14.37 8.67 14C8.67 13.63 8.37 13.33 8 13.33C7.63 13.33 7.33 13.63 7.33 14C7.33 14.37 7.63 14.67 8 14.67Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-gray-400">
            <path d="M11.67 2.33H10.5V1.17C10.5 0.52 9.98 0 9.33 0C8.68 0 8.17 0.52 8.17 1.17V2.33H5.83V1.17C5.83 0.52 5.32 0 4.67 0C4.02 0 3.5 0.52 3.5 1.17V2.33H2.33C1.01 2.33 0 3.34 0 4.67V11.67C0 12.99 1.01 14 2.33 14H11.67C12.99 14 14 12.99 14 11.67V4.67C14 3.34 12.99 2.33 11.67 2.33ZM12.83 11.67C12.83 12.19 12.19 12.83 11.67 12.83H2.33C1.81 12.83 1.17 12.19 1.17 11.67V6.42H12.83V11.67ZM12.83 5.25H1.17V4.67C1.17 4.15 1.81 3.5 2.33 3.5H3.5V4.67C3.5 5.32 4.02 5.83 4.67 5.83C5.32 5.83 5.83 5.32 5.83 4.67V3.5H8.17V4.67C8.17 5.32 8.68 5.83 9.33 5.83C9.98 5.83 10.5 5.32 10.5 4.67V3.5H11.67C12.19 3.5 12.83 4.15 12.83 4.67V5.25Z" fill="currentColor"/>
          </svg>
          <span className="text-xs text-gray-500">Last Updated: {patient.lastAppointment}</span>
        </div>

        {patient.phoneNumber && (
          <div className="flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-gray-400">
              <path d="M13.5 10.5V12.5C13.5 13.05 13.05 13.5 12.5 13.5C5.87 13.5 0.5 8.13 0.5 1.5C0.5 0.95 0.95 0.5 1.5 0.5H3.5C4.05 0.5 4.5 0.95 4.5 1.5V3.5C4.5 4.05 4.05 4.5 3.5 4.5H2.5C2.5 7.81 5.19 10.5 8.5 10.5V9.5C8.5 8.95 8.95 8.5 9.5 8.5H11.5C12.05 8.5 12.5 8.95 12.5 9.5V11.5C12.5 12.05 12.05 12.5 11.5 12.5H10.5V10.5H13.5Z" fill="currentColor"/>
            </svg>
            <span className="text-xs text-gray-500">{patient.phoneNumber}</span>
          </div>
        )}

        <div className="flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-gray-400">
            <path d="M7 0C3.13 0 0 3.13 0 7C0 10.87 7 14 7 14C7 14 14 10.87 14 7C14 3.13 10.87 0 7 0ZM7 9.33C5.53 9.33 4.33 8.13 4.33 6.67C4.33 5.2 5.53 4 7 4C8.47 4 9.67 5.2 9.67 6.67C9.67 8.13 8.47 9.33 7 9.33Z" fill="currentColor"/>
          </svg>
          <span className="text-xs text-gray-500 truncate">{patient.location}</span>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-black">Patient Grid</h1>
                <div className="bg-blue-50 border border-blue-200 rounded-md px-2 py-1">
                  <span className="text-xs font-medium text-black">Total Patients : {totalPatients.toString().padStart(3, '0')}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Search Input */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search patients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <svg 
                    className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>

                {/* Filter Dropdown */}
                <div className="relative filter-dropdown">
                  <button 
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M13.5 0.5H0.5C0.22 0.5 0 0.72 0 1C0 1.28 0.22 1.5 0.5 1.5H13.5C13.78 1.5 14 1.28 14 1C14 0.72 13.78 0.5 13.5 0.5ZM13.5 6.5H0.5C0.22 6.5 0 6.72 0 7C0 7.28 0.22 7.5 0.5 7.5H13.5C13.78 7.5 14 7.28 14 7C14 6.72 13.78 6.5 13.5 6.5ZM13.5 12.5H0.5C0.22 12.5 0 12.72 0 13C0 13.28 0.22 13.5 0.5 13.5H13.5C13.78 13.5 14 13.28 14 13C14 12.72 13.78 12.5 13.5 12.5Z" fill="currentColor"/>
                    </svg>
                    <span className="text-sm text-black">Filter</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setGenderFilter('');
                            setShowFilterDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${genderFilter === '' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                        >
                          All Genders
                        </button>
                        <button
                          onClick={() => {
                            setGenderFilter('Male');
                            setShowFilterDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${genderFilter === 'Male' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                        >
                          Male
                        </button>
                        <button
                          onClick={() => {
                            setGenderFilter('Female');
                            setShowFilterDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${genderFilter === 'Female' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                        >
                          Female
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Grid View Toggle */}
                <button 
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-blue-100' : 'bg-white border border-gray-300'}`}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M0 0H6V6H0V0ZM8 0H14V6H8V0ZM0 8H6V14H0V8ZM8 8H14V14H8V8Z" fill="currentColor"/>
                  </svg>
                </button>
                
                {/* New Patient Button */}
                <button
                  onClick={() => navigate('/patient/new')}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <path d="M6.5 0C6.78 0 7 0.22 7 0.5V6H12.5C12.78 6 13 6.22 13 6.5C13 6.78 12.78 7 12.5 7H7V12.5C7 12.78 6.78 13 6.5 13C6.22 13 6 12.78 6 12.5V7H0.5C0.22 7 0 6.78 0 6.5C0 6.22 0.22 6 0.5 6H6V0.5C6 0.22 6.22 0 6.5 0Z" fill="currentColor"/>
                  </svg>
                  <span className="text-sm font-semibold">New Patient</span>
                </button>
              </div>
            </div>
          </div>

          {/* Patient Grid */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-600">{error}</p>
              <button 
                onClick={fetchPatients}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          ) : currentPatients.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No patients found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || genderFilter ? 'Try adjusting your search or filter criteria.' : 'Get started by adding a new patient.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
              {currentPatients.map((patient) => (
                <PatientCard key={patient.id} patient={patient} />
              ))}
            </div>
          )}

          {/* Pagination */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">Show</span>
                <select 
                  value={itemsPerPage} 
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                  <option value={15}>15</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-500">per page</span>
              </div>
              
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500">
                  {startIndex + 1}-{Math.min(endIndex, totalPatients)} of {totalPatients}
                </span>
                
                <div className="flex items-center gap-1">
                  <button 
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-8 h-8 rounded-md text-sm font-medium ${
                          currentPage === pageNum 
                            ? 'bg-blue-100 text-blue-900' 
                            : 'text-gray-500 hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button 
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MyPatients;
